import React, { useState, useEffect } from "react";
import { useLanguage } from "./LanguageContext";
import { auth, db } from "./firebase";
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, collection, getDocs, query, orderBy } from "firebase/firestore";

const SCHEMES = [
  { id: "pmay", title: "PM Awas Yojana", desc: "Garib parivaron ko pakka ghar", category: "Housing", link: "https://pmayg.nic.in" },
  { id: "pmksn", title: "PM Kisan Samman Nidhi", desc: "Kisanon ko ₹6000/year direct bank mein", category: "Agriculture", link: "https://pmkisan.gov.in" },
  { id: "ayushman", title: "Ayushman Bharat", desc: "₹5 lakh tak free hospital treatment", category: "Health", link: "https://pmjay.gov.in" },
  { id: "nsp", title: "National Scholarship Portal", desc: "Students ke liye scholarship apply karein", category: "Education", link: "https://scholarships.gov.in" },
  { id: "mudra", title: "PM Mudra Yojana", desc: "Chhote business ke liye ₹10 lakh tak loan", category: "Business", link: "https://mudra.org.in" },
  { id: "ujjwala", title: "PM Ujjwala Yojana", desc: "BPL mahilaon ko free LPG connection", category: "Energy", link: "https://pmuy.gov.in" },
  { id: "rti", title: "RTI — Soochna ka Adhikar", desc: "Sarkar se koi bhi jaankari maango", category: "Rights", link: "https://rtionline.gov.in" },
  { id: "mnrega", title: "MNREGA", desc: "100 din guaranteed rojgar guarantee", category: "Employment", link: "https://nrega.nic.in" },
];

const QUIZ_QUESTIONS = [
  { q: "Bharat ke pehle Pradhan Mantri kaun the?", options: ["Sardar Patel", "Jawaharlal Nehru", "Rajendra Prasad", "B.R. Ambedkar"], ans: 1 },
  { q: "RTI Act kab laagu hua?", options: ["2000", "2003", "2005", "2007"], ans: 2 },
  { q: "Panchayati Raj mein sabse choti unit kaun si hai?", options: ["Zila Parishad", "Panchayat Samiti", "Gram Sabha", "Gram Panchayat"], ans: 3 },
  { q: "Ayushman Bharat scheme mein kitna health cover milta hai?", options: ["₹1 lakh", "₹2 lakh", "₹5 lakh", "₹10 lakh"], ans: 2 },
  { q: "ISRO ka full form kya hai?", options: ["Indian Space Research Organisation", "Indian Science Research Organisation", "International Space Research Organisation", "Indian Solar Research Organisation"], ans: 0 },
  { q: "Bharat mein kitne rajya hain (2024)?", options: ["26", "28", "29", "30"], ans: 1 },
  { q: "PM Kisan Samman Nidhi mein kitni rashi milti hai?", options: ["₹3000", "₹4000", "₹6000", "₹8000"], ans: 2 },
  { q: "Mudra Yojana ke antargat maximum loan kitna milta hai?", options: ["₹5 lakh", "₹10 lakh", "₹15 lakh", "₹20 lakh"], ans: 1 },
];

const DYK_FACTS = [
  { fact: "Bharat duniya ka sabse bada loktantra hai jisme 96 crore se zyada registered voters hain." },
  { fact: "RTI ke zariye aap kisi bhi sarkari kaam ki jaankari 30 din mein maang sakte hain." },
  { fact: "MNREGA scheme ke tehat ek parivaar ko saal mein 100 din ka guaranteed kaam milta hai." },
  { fact: "Ayushman Bharat duniya ki sabse badi health insurance yojana hai." },
  { fact: "ISRO ne 2023 mein Chandrayaan-3 se Chand ke dakshin dhruv par safaltapoorvak landing ki." },
  { fact: "Bharat ka Samvidhan duniya ka sabse lamba likhit samvidhan hai jisme 448 Articles hain." },
];

const SCHEME_CATEGORIES = ["All", "Housing", "Agriculture", "Health", "Education", "Business", "Employment", "Rights", "Energy"];
const PAGE_SIZE = 3;

export default function Knowledge() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("schemes");
  const [schemeFilter, setSchemeFilter] = useState("All");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [activeChip, setActiveChip] = useState(t.chips[0]);
  const [visibleArticles, setVisibleArticles] = useState(PAGE_SIZE);
  const [openArticle, setOpenArticle] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [quizIdx, setQuizIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [dykIdx, setDykIdx] = useState(0);
  const [firestoreArticles, setFirestoreArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);

  const uid = auth.currentUser?.uid;

  useEffect(() => {
    fetchBookmarks();
    fetchArticles();
    setDykIdx(Math.floor(Math.random() * DYK_FACTS.length));
    setQuizIdx(Math.floor(Math.random() * QUIZ_QUESTIONS.length));
  }, []);

  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [schemeFilter]);
  useEffect(() => { setVisibleArticles(PAGE_SIZE); }, [activeChip]);

  const fetchBookmarks = async () => {
    try {
      const snap = await getDoc(doc(db, "users", uid));
      if (snap.exists()) setBookmarks(snap.data().bookmarks || []);
    } catch {}
  };

  const fetchArticles = async () => {
    try {
      const snap = await getDocs(query(collection(db, "knowledgeArticles"), orderBy("createdAt", "desc")));
      setFirestoreArticles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {}
    setArticlesLoading(false);
  };

  const toggleBookmark = async (articleKey) => {
    const isBookmarked = bookmarks.includes(articleKey);
    setBookmarks(isBookmarked ? bookmarks.filter(b => b !== articleKey) : [...bookmarks, articleKey]);
    try {
      await updateDoc(doc(db, "users", uid), {
        bookmarks: isBookmarked ? arrayRemove(articleKey) : arrayUnion(articleKey),
      });
    } catch {}
  };

  const handleAnswer = (idx) => {
    if (selected !== null) return;
    setSelected(idx);
    if (idx === QUIZ_QUESTIONS[quizIdx % QUIZ_QUESTIONS.length].ans) setScore(s => s + 1);
  };

  const nextQuestion = () => {
    if (quizIdx + 1 >= QUIZ_QUESTIONS.length) { setQuizDone(true); return; }
    setQuizIdx(p => p + 1); setSelected(null);
  };

  const resetQuiz = () => { setQuizIdx(0); setSelected(null); setScore(0); setQuizDone(false); };

  const filteredSchemes = schemeFilter === "All" ? SCHEMES : SCHEMES.filter(s => s.category === schemeFilter);
  const visibleSchemes = filteredSchemes.slice(0, visibleCount);
  const hasMore = visibleCount < filteredSchemes.length;
  const q = QUIZ_QUESTIONS[quizIdx % QUIZ_QUESTIONS.length];

  const hardcodedArticles = (t.articles[activeChip] || []).map(a => ({ ...a, id: `${activeChip}-${a.title}`, category: activeChip, source: "hardcoded" }));
  const fsArticlesForChip = firestoreArticles.filter(a => a.category === activeChip);
  const displayArticles = fsArticlesForChip.length > 0 ? fsArticlesForChip : hardcodedArticles;

  if (openArticle) {
    const key = openArticle.id || `${activeChip}-${openArticle.title}`;
    const isBookmarked = bookmarks.includes(key);
    return (
      <div>
        <div style={S.articleHeader}>
          <button onClick={() => setOpenArticle(null)} style={S.backBtn}>← Wapas</button>
          <button onClick={() => toggleBookmark(key)} style={S.bookmarkBtn}>
            {isBookmarked ? "Saved" : "Save"}
          </button>
        </div>
        <div style={S.articleCard}>
          <h2 style={S.articleTitle}>{openArticle.title}</h2>
          <p style={S.articleMeta}>{openArticle.category || activeChip} · {openArticle.read || openArticle.readTime}</p>
          <p style={S.articleBody}>{openArticle.desc}</p>
          {openArticle.content && (
            <div style={S.articleContent}>
              <p style={S.articleBody}>{openArticle.content}</p>
            </div>
          )}
          {!openArticle.content && (
            <div style={S.articleContent}>
              <p style={S.articleBody}>Yeh ek important topic hai jo har Indian citizen ko pata hona chahiye.</p>
              <p style={S.articleBody}>Agar aur information chahiye toh sanstha ke admin se sampark karein.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={S.title}>{t.knowledgeTitle}</h2>
      <p style={S.subtitle}>{t.knowledgeSubtitle}</p>

      <div style={S.tabRow}>
        {[{ id: "schemes", label: "Schemes" }, { id: "quiz", label: "Quiz" }, { id: "dyk", label: "Did You Know" }, { id: "articles", label: "Articles" }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ ...S.tabBtn, background: activeTab === tab.id ? "#0D1B3E" : "#fff", color: activeTab === tab.id ? "#fff" : "#0D1B3E" }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "schemes" && (
        <div>
          <select value={schemeFilter} onChange={e => setSchemeFilter(e.target.value)} style={S.categorySelect}>
            {SCHEME_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat === "All" ? `Sab Schemes (${SCHEMES.length})` : cat}</option>
            ))}
          </select>

          {visibleSchemes.map(scheme => (
            <div key={scheme.id} style={S.schemeCard}>
              <p style={S.schemeTitle}>{scheme.title}</p>
              <p style={S.schemeDesc}>{scheme.desc}</p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={S.schemeCat}>{scheme.category}</span>
                <a href={scheme.link} target="_blank" rel="noreferrer" style={S.schemeLink}>Website Kholo →</a>
              </div>
            </div>
          ))}

          {hasMore && (
            <>
              <button onClick={() => setVisibleCount(c => c + PAGE_SIZE)} style={S.loadMoreBtn}>
                Aur Dekhein ↓
              </button>
              <p style={S.countText}>{visibleSchemes.length} / {filteredSchemes.length} schemes</p>
            </>
          )}
        </div>
      )}

      {activeTab === "quiz" && (
        <div>
          {quizDone ? (
            <div style={S.quizResult}>
              <p style={S.quizScore}>Score: {score}/{QUIZ_QUESTIONS.length}</p>
              <p style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 20 }}>
                {score >= 6 ? "Bahut badiya!" : score >= 4 ? "Acha kiya!" : "Practice karo"}
              </p>
              <button onClick={resetQuiz} style={S.quizBtn}>Dobara Khelein</button>
            </div>
          ) : (
            <div style={S.quizCard}>
              <div style={S.quizProgress}>
                <span style={{ fontSize: 12, color: "#9CA3AF" }}>Question {quizIdx + 1}/{QUIZ_QUESTIONS.length}</span>
                <span style={{ fontSize: 12, color: "#0D1B3E", fontWeight: 700 }}>Score: {score}</span>
              </div>
              <div style={S.progressBar}><div style={{ ...S.progressFill, width: `${(quizIdx / QUIZ_QUESTIONS.length) * 100}%` }} /></div>
              <p style={S.quizQ}>{q.q}</p>
              {q.options.map((opt, i) => {
                let bg = "#F7F7F9", color = "#0D1B3E";
                if (selected !== null) {
                  if (i === q.ans) { bg = "#F0FDF4"; color = "#16A34A"; }
                  else if (i === selected) { bg = "#FEF2F2"; color = "#DC2626"; }
                }
                return (
                  <button key={i} onClick={() => handleAnswer(i)}
                    style={{ ...S.optionBtn, background: bg, color, border: selected !== null && i === q.ans ? "1.5px solid #16A34A" : "1px solid #ECECEE" }}>
                    <span style={S.optLetter}>{["A","B","C","D"][i]}</span>{opt}
                  </button>
                );
              })}
              {selected !== null && (
                <button onClick={nextQuestion} style={S.quizBtn}>
                  {quizIdx + 1 >= QUIZ_QUESTIONS.length ? "Result Dekho" : "Agla Sawaal →"}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "dyk" && (
        <div>
          <div style={S.dykCard}>
            <p style={S.dykFact}>{DYK_FACTS[dykIdx].fact}</p>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={() => setDykIdx(p => (p - 1 + DYK_FACTS.length) % DYK_FACTS.length)} style={S.dykNav}>← Pehle</button>
              <button onClick={() => setDykIdx(p => (p + 1) % DYK_FACTS.length)} style={{ ...S.dykNav, background: "rgba(255,255,255,0.18)" }}>Agla →</button>
            </div>
            <p style={{ textAlign: "center", fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 8 }}>{dykIdx + 1} / {DYK_FACTS.length}</p>
          </div>
          <p style={S.sectionLabel}>Saari Facts</p>
          {DYK_FACTS.map((f, i) => (
            <div key={i} onClick={() => setDykIdx(i)}
              style={{ ...S.dykListItem, background: i === dykIdx ? "#F7F7F9" : "#fff", border: i === dykIdx ? "1px solid #0D1B3E20" : "1px solid #ECECEE" }}>
              <p style={{ margin: 0, fontSize: 12, color: "#374151" }}>{f.fact.slice(0, 70)}...</p>
            </div>
          ))}
        </div>
      )}

      {activeTab === "articles" && (
        <div>
          <select value={activeChip} onChange={e => setActiveChip(e.target.value)} style={S.categorySelect}>
            {t.chips.map(chip => <option key={chip} value={chip}>{chip}</option>)}
          </select>

          {bookmarks.length > 0 && (
            <div style={S.bookmarkNotice}>{bookmarks.length} article{bookmarks.length > 1 ? "s" : ""} save kiya hai</div>
          )}

          {articlesLoading && <p style={{ textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>Loading...</p>}

          {!articlesLoading && displayArticles.slice(0, visibleArticles).map((art, i) => {
            const key = art.id || `${activeChip}-${art.title}`;
            const isBookmarked = bookmarks.includes(key);
            return (
              <div key={i} style={S.articleListCard}>
                <div style={S.cardTop}>
                  <h4 style={S.cardTitle}>{art.title}</h4>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                    <span style={S.readBadge}>{art.read || art.readTime || "3 min"}</span>
                    <button onClick={() => toggleBookmark(key)} style={S.bookmarkIconBtn}>{isBookmarked ? "★" : "☆"}</button>
                  </div>
                </div>
                <p style={S.cardDesc}>{art.desc}</p>
                <button onClick={() => setOpenArticle(art)} style={S.readBtn}>{t.readMore}</button>
              </div>
            );
          })}

          {!articlesLoading && visibleArticles < displayArticles.length && (
            <>
              <button onClick={() => setVisibleArticles(c => c + PAGE_SIZE)} style={S.loadMoreBtn}>
                Aur Dekhein ↓
              </button>
              <p style={S.countText}>{Math.min(visibleArticles, displayArticles.length)} / {displayArticles.length} articles</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const S = {
  title: { fontSize: 19, fontWeight: 700, color: "#0D1B3E", margin: "0 0 4px" },
  subtitle: { fontSize: 13, color: "#9CA3AF", marginBottom: 14 },
  tabRow: { display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 },
  tabBtn: { padding: "8px 12px", border: "1px solid #ECECEE", borderRadius: 10, fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" },
  categorySelect: { width: "100%", padding: "12px 14px", borderRadius: 12, border: "1px solid #ECECEE", background: "#FAFAFA", fontSize: 13, color: "#0D1B3E", marginBottom: 16, boxSizing: "border-box" },
  schemeCard: { background: "#fff", border: "1px solid #ECECEE", borderRadius: 14, padding: "14px 16px", marginBottom: 10 },
  schemeTitle: { margin: "0 0 3px", fontSize: 13, fontWeight: 600, color: "#0D1B3E" },
  schemeDesc: { margin: "0 0 10px", fontSize: 12, color: "#9CA3AF" },
  schemeCat: { fontSize: 10, fontWeight: 600, background: "#F7F7F9", color: "#0D1B3E", padding: "3px 8px", borderRadius: 6 },
  schemeLink: { fontSize: 12, fontWeight: 600, color: "#0D1B3E", textDecoration: "none" },
  loadMoreBtn: { width: "100%", padding: 12, background: "none", border: "1px dashed #ECECEE", borderRadius: 12, fontSize: 12, fontWeight: 600, color: "#9CA3AF", cursor: "pointer", marginTop: 4 },
  countText: { textAlign: "center", fontSize: 11, color: "#D1D5DB", marginTop: 8 },
  quizCard: { background: "#fff", borderRadius: 16, padding: 20, border: "1px solid #ECECEE" },
  quizProgress: { display: "flex", justifyContent: "space-between", marginBottom: 8 },
  progressBar: { height: 4, background: "#F7F7F9", borderRadius: 4, marginBottom: 16, overflow: "hidden" },
  progressFill: { height: "100%", background: "#0D1B3E", borderRadius: 4, transition: "width 0.3s" },
  quizQ: { fontSize: 14, fontWeight: 600, color: "#0D1B3E", marginBottom: 16, lineHeight: 1.5 },
  optionBtn: { width: "100%", padding: "12px 14px", borderRadius: 10, cursor: "pointer", textAlign: "left", fontSize: 13, marginBottom: 8, display: "flex", alignItems: "center", gap: 10 },
  optLetter: { width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.06)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 },
  quizBtn: { width: "100%", marginTop: 12, padding: "12px", background: "#0D1B3E", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  quizResult: { background: "#fff", borderRadius: 16, padding: 32, textAlign: "center", border: "1px solid #ECECEE" },
  quizScore: { fontSize: 26, fontWeight: 700, color: "#0D1B3E", margin: "8px 0 4px" },
  dykCard: { background: "linear-gradient(135deg, #0D1B3E, #1A2F5A)", borderRadius: 16, padding: 24, marginBottom: 16 },
  dykFact: { fontSize: 14, lineHeight: 1.7, color: "#fff", textAlign: "center", margin: 0 },
  dykNav: { flex: 1, padding: "10px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 10, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  dykListItem: { borderRadius: 12, padding: "12px 14px", marginBottom: 8, cursor: "pointer" },
  sectionLabel: { fontSize: 12, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5, margin: "16px 0 8px 4px" },
  chipsRow: { display: "flex", gap: 8, overflowX: "auto", paddingBottom: 12, marginBottom: 12 },
  chip: { padding: "8px 16px", borderRadius: 20, cursor: "pointer", whiteSpace: "nowrap", fontSize: 12, fontWeight: 600, flexShrink: 0 },
  bookmarkNotice: { background: "#F7F7F9", borderRadius: 10, padding: "8px 12px", fontSize: 12, color: "#0D1B3E", marginBottom: 12 },
  articleListCard: { background: "#fff", borderRadius: 14, padding: 16, marginBottom: 10, border: "1px solid #ECECEE" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 },
  cardTitle: { margin: 0, fontSize: 14, fontWeight: 600, color: "#0D1B3E", flex: 1 },
  readBadge: { fontSize: 10, color: "#9CA3AF", fontWeight: 600, marginLeft: 8, whiteSpace: "nowrap" },
  bookmarkIconBtn: { background: "none", border: "none", fontSize: 14, cursor: "pointer", padding: 0, color: "#0D1B3E" },
  cardDesc: { margin: "0 0 10px", fontSize: 12, color: "#9CA3AF" },
  readBtn: { background: "none", border: "1px solid #0D1B3E", color: "#0D1B3E", borderRadius: 8, padding: "6px 14px", fontSize: 11, fontWeight: 600, cursor: "pointer" },
  articleHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 },
  backBtn: { background: "#F7F7F9", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 12, cursor: "pointer", color: "#0D1B3E", fontWeight: 600 },
  bookmarkBtn: { background: "#F7F7F9", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 12, cursor: "pointer", color: "#0D1B3E", fontWeight: 600 },
  articleCard: { background: "#fff", borderRadius: 16, padding: 20, border: "1px solid #ECECEE" },
  articleTitle: { margin: "0 0 6px", fontSize: 17, fontWeight: 700, color: "#0D1B3E" },
  articleMeta: { margin: "0 0 14px", fontSize: 12, color: "#9CA3AF" },
  articleContent: { borderTop: "1px solid #ECECEE", paddingTop: 14, marginTop: 14 },
  articleBody: { fontSize: 13, color: "#374151", lineHeight: 1.8, margin: "0 0 12px" },
};